// Updated tags.js
import api from './api-config';

export const addTagToTransaction = async (transactionId, tagId) => {
    try {
        const response = await api.post(`/tags/${transactionId}/${tagId}`);
        return response.data;
    } catch (error) {
        console.error('Error adding tag to transaction:', error);
        throw error;
    }
};

export const removeTagFromTransaction = async (transactionId, tagId) => {
    try {
        const response = await api.delete(`/tags/${transactionId}/${tagId}`);
        return response.data;
    } catch (error) {
        console.error('Error removing tag from transaction:', error);
        throw error;
    }
};

export const createTag = async (tagData) => {
    try {
        // Convert parent_path to parent_id if needed for backward compatibility
        const payload = { ...tagData };

        // API now expects parent_id instead of parent path in the name
        if (payload.parent_id === undefined && payload.parent) {
            delete payload.parent;
        }

        const response = await api.post('/tags', payload);
        return response.data;
    } catch (error) {
        console.error('Error creating tag:', error);
        throw error;
    }
};

export const getTags = async (loadTransactionCounts = false, params = {}) => {
    try {
        const response = await api.get(`/tags?include_transaction_counts=${loadTransactionCounts}`, {params});
        return response.data;
    } catch (error) {
        console.error('Error fetching tags:', error);
        throw error;
    }
};

export const getTagChildren = async (tagId) => {
    try {
        const response = await api.get(`/tags/${tagId}/children`);
        return response.data;
    } catch (error) {
        console.error('Error fetching tag children:', error);
        throw error;
    }
};

export const deleteTag = async (tagId, recursive = true) => {
    try {
        const response = await api.delete(`/tags/${tagId}?recursive=${recursive}`);
        return response.data;
    } catch (error) {
        console.error('Error deleting tag:', error);
        throw error;
    }
};

export const bulkTagTransactions = async (transactionIds, tagId) => {
    const txIds = Array.isArray(transactionIds) ? transactionIds : [transactionIds];

    try {
        const response = await api.post('/tags/bulk', {
            transaction_ids: txIds, tag_id: tagId
        });
        return response.data;
    } catch (error) {
        console.error('Error bulk tagging transactions:', error);
        throw error;
    }
};

export const addTagToTransactions = async (transactionIds, tagId) => {
    return bulkTagTransactions(transactionIds, tagId);
};

export const addMultipleTagsToTransactions = async (transactionIds, tagIds) => {
    const tIds = Array.isArray(tagIds) ? tagIds : [tagIds];

    try {
        const results = [];
        for (const tagId of tIds) {
            const result = await bulkTagTransactions(transactionIds, tagId);
            results.push(result);
        }
        return results;
    } catch (error) {
        console.error('Error adding multiple tags to transactions:', error);
        throw error;
    }
};

export const getTagTypes = () => {
    return [
        {value: 'REVENUE', label: 'Revenue'},
        {value: 'EXPENSE', label: 'Expense'},
        {value: null, label: 'Unspecified'}
    ];
};

export const updateTag = async (tagId, tagData) => {
    try {
        const response = await api.put(`/tags/${tagId}`, tagData);
        return response.data;
    } catch (error) {
        console.error('Error updating tag:', error);
        throw error;
    }
};

// ============================================================================
// TAG TEMPLATES
// ============================================================================

/**
 * Get list of available tag templates
 * @returns {Promise<Array>} List of template summaries
 */
export const getTagTemplates = async () => {
    try {
        const response = await api.get('/tags/templates');
        return response.data;
    } catch (error) {
        console.error('Error fetching tag templates:', error);
        throw error;
    }
};

/**
 * Get full details of a specific template including tag hierarchy
 * @param {string} templateId - Template ID (e.g., 'saas_business')
 * @returns {Promise<Object>} Template with full tag hierarchy
 */
export const getTagTemplate = async (templateId) => {
    try {
        const response = await api.get(`/tags/templates/${templateId}`);
        return response.data;
    } catch (error) {
        console.error(`Error fetching template ${templateId}:`, error);
        throw error;
    }
};

/**
 * Apply a tag template to the current organization
 * @param {string} templateId - Template ID to apply
 * @param {boolean} clearExisting - If true, delete existing tags first
 * @returns {Promise<Object>} Result summary
 */
export const applyTagTemplate = async (templateId, clearExisting = false) => {
    try {
        const response = await api.post(
            `/tags/templates/${templateId}/apply?clear_existing=${clearExisting}`
        );
        return response.data;
    } catch (error) {
        console.error(`Error applying template ${templateId}:`, error);
        throw error;
    }
};

/**
 * Get summary of organization's current tags
 * @returns {Promise<Object>} Tag summary with counts
 */
export const getTagSummary = async () => {
    try {
        const response = await api.get('/tags/summary');
        return response.data;
    } catch (error) {
        console.error('Error fetching tag summary:', error);
        throw error;
    }
};
